import React, { FormEvent, useEffect, useState } from 'react'
import { Form } from 'react-router-dom';
import { Alert, Button, IconButton } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { IExpense, IExpenseEntity, IExpenseEntityAssignmentRequest } from '../../types/expense';
import { ISelectOption } from '../../types/list';
import { IVendorLight } from '../../types/vendor';

import { AppConfig } from '../../config/appConfig';
import { ErrorProvider } from '../../classes/errorProvider';
import { ExpenseService } from '../../services/expenseService';
import { useListContext } from '../../contexts/listContext';

import MultiSelectionManager from '../shared/multiSelectionManager';
import Spinner from '../spinner';
import WorkingModal, { IWorkingModalProps } from '../workingModal';

import '../../styles/expenseEntityForm.scss';

export interface IExpenseVendorFormProps {
	expense: IExpense;
	entities: IVendorLight[];
	isLoadingEntities: boolean;
	onAssignedEntitiesChanged?: (entityTypeId: number, assignedEntities: IExpenseEntity[]) => void;
}

const ExpenseVendorForm: React.FunctionComponent<IExpenseVendorFormProps> = ({ expense, entities, isLoadingEntities, onAssignedEntitiesChanged }) => {
	const listContext = useListContext();
	const entityTypeName = "Vendor";
	const [isSaving, setIsSaving] = useState(false);
	const [successMessage, setSuccessMessage] = useState<string | undefined>();
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [entityTypeErrorMessage, setEntityTypeErrorMessage] = useState<string | undefined>();
	const [entityTypeId, setEntityTypeId] = useState<number>(+listContext.entityTypes?.find(e => e.name == entityTypeName)?.value);
	const [assignedIds, setAssignedIds] = useState<number[]>(expense.assignedEntities.filter(e => e.entityTypeId == entityTypeId).map(e => e.entityId));
	const [workingModalParams, setWorkingModalParams] = useState<IWorkingModalProps | undefined>(undefined);

	const isDataReady = (): boolean => {
		return (entityTypeId && !isLoadingEntities && Array.isArray(entities) && Array.isArray(assignedIds));
	}

	useEffect(() => {
		setEntityTypeErrorMessage(undefined);
		if (isNaN(entityTypeId)) {
			setEntityTypeErrorMessage(`Entity Type "${entityTypeName}" does not exist.`);
		}
	}, [entityTypeId]);

	useEffect(() => {
		if (entityTypeId && expense?.assignedEntities) {
			setAssignedIds(expense.assignedEntities.filter(e => e.entityTypeId == entityTypeId).map(e => e.entityId));
		}
		else {
			setAssignedIds([]);
		}
	}, [expense, entityTypeId])

	const handleSelectionChanged = (newAssignedIds: number[]) => {
		setAssignedIds(newAssignedIds ?? []);
	}

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setErrorMessage(undefined);
		setIsSaving(true);

		setWorkingModalParams({ params: { content: `Saving ${entityTypeName.toLowerCase()} assignments...` } });

		const request: IExpenseEntityAssignmentRequest = {
			expenseId: expense.id,
			entityTypeId: entityTypeId,
			selectedEntityIds: assignedIds ?? []
		};

		ExpenseService.setAssignedEntities(request)
			.then((response) => {
				const updatedEntities: IExpenseEntity[] = response.data;
				console.log('ExpenseService.setAssignedEntities: SUCCESS', response);

				setSuccessMessage(`Assigned ${entityTypeName}s have been updated.`);
				setTimeout(() => {
					setSuccessMessage(undefined);
				}, AppConfig.editSuccessToastMs);
				onAssignedEntitiesChanged?.(entityTypeId, updatedEntities);
			})
			.catch((error) => {
				console.log('ExpenseService.setAssignedEntities: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setIsSaving(false);
				setWorkingModalParams(undefined);
				window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
			});
	}

	return (
		<section className="hwi-expense-entity-form">
			<Spinner dependency={!isDataReady()} text={`Loading ${entityTypeName}s...`}>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					{successMessage &&
						<Alert className="alert-success"
							severity="success"
							action={<IconButton onClick={() => { setSuccessMessage(undefined); }} size='small'>X</IconButton>}>
							{successMessage}
						</Alert>
					}
					{entityTypeErrorMessage &&
						<Alert className="alert-error"
							severity="error"
							action={<IconButton onClick={() => { setEntityTypeErrorMessage(undefined); }} size='small'>X</IconButton>}>
							{entityTypeErrorMessage}
						</Alert>
					}
					{errorMessage &&
						<Alert className="alert-error"
							severity="error"
							action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>
							{errorMessage}
						</Alert>
					}
					<Form onSubmit={(event) => handleSubmit(event)}>
						<div className="row manager">
							<MultiSelectionManager								
								options={entities?.map(v => ({ value: v.id, name: v.name, isActive: v.status == "Active" } as ISelectOption)) ?? []}
								selectedIds={assignedIds}
								hideInactive={true}
								itemType={entityTypeName}
								onSelectionChanged={handleSelectionChanged}
							></MultiSelectionManager>
						</div>
						<div className="row controls bottom">
							<Button type="submit" className="save-button" disabled={isSaving} variant="contained">{isSaving ? "Saving..." : "Save"}</Button>
						</div>
					</Form>
					{/* <Json data={formFields}></Json> */}
				</LocalizationProvider>
			</Spinner>
			<WorkingModal params={workingModalParams?.params}></WorkingModal>
		</section>
	)
}

export default ExpenseVendorForm;